import React, { useState, useContext } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Home, 
  Settings, 
  Shield, 
  Users, 
  School, 
  BookOpen,
  FileText, 
  Flag, 
  AlertTriangle,
  MessageSquare,
  History
} from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import ThemeContext from '../contexts/ThemeContext';
import { THEMES } from '../utils/theme';
import MeritDisplay from '../components/MeritDisplay';

/**
 * Admin layout component for admin pages
 * Includes admin navigation, header, and content area
 */
const AdminLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Theme toggle handler
  const handleThemeToggle = () => {
    // Cycle through themes: default -> dark -> light -> default
    const themes = [THEMES.DEFAULT, THEMES.DARK, THEMES.LIGHT];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  // Ensure only admins can access
  // Consider updating this to include moderators if they should have admin dashboard access
  if (user?.role !== 'admin') {
    navigate('/school');
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-red-900 text-white transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:w-64`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between p-4 border-b border-red-800">
          <div className="flex items-center space-x-2">
            <Shield size={24} />
            <span className="text-xl font-bold">Admin Dashboard</span>
          </div>
          <button 
            className="p-1 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-red-600"
            onClick={toggleSidebar}
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Sidebar content */}
        <nav className="mt-5 px-2">
          <NavLink 
            to="/admin" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Home className="mr-3" size={20} />
            <span>Dashboard</span>
          </NavLink>
          
          <NavLink 
            to="/admin/users" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Users className="mr-3" size={20} />
            <span>Users</span>
          </NavLink>
          
          <NavLink 
            to="/admin/schools" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <School className="mr-3" size={20} />
            <span>Schools</span>
          </NavLink>

          <NavLink 
            to="/admin/requests" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <MessageSquare className="mr-3" size={20} />
            <span>Requests</span>
          </NavLink>
          
          {/* Courses Link */}
          <NavLink 
            to="/admin/courses" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <BookOpen className="mr-3" size={20} />
            <span>Courses</span>
          </NavLink>
          
          {/* New Professors Link */}
          <NavLink 
            to="/admin/professors" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <User className="mr-3" size={20} />
            <span>Professors</span>
          </NavLink>
          
          <NavLink 
            to="/admin/materials" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <FileText className="mr-3" size={20} />
            <span>Materials</span>
          </NavLink>
          
          <NavLink 
            to="/admin/reports" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Flag className="mr-3" size={20} />
            <span>Reports</span>
          </NavLink>
          
          <NavLink 
            to="/admin/audit-logs" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                isActive 
                  ? 'bg-red-800 text-white' 
                  : 'text-gray-100 hover:bg-red-800'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <History className="mr-3" size={20} />
            <span>Audit Logs</span>
          </NavLink>
          
          <div className="border-t border-red-800 my-4"></div>
          
          <NavLink 
            to="/school" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-red-800`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Home className="mr-3" size={20} />
            <span>Return to App</span>
          </NavLink>
          
          <NavLink 
            to="/profile" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-red-800`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <User className="mr-3" size={20} />
            <span>Profile</span>
          </NavLink>
          
          <NavLink 
            to="/settings" 
            className={({ isActive }) => 
              `flex items-center px-4 py-2 mt-2 text-sm rounded-lg text-gray-100 hover:bg-red-800`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Settings className="mr-3" size={20} />
            <span>Settings</span>
          </NavLink>
          
          <button 
            onClick={handleLogout}
            className="flex w-full items-center px-4 py-2 mt-2 text-sm text-gray-100 rounded-lg hover:bg-red-800"
          >
            <LogOut className="mr-3" size={20} />
            <span>Logout</span>
          </button>
        </nav>
        {/* Alpha Version Disclaimer */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-red-700 text-white rounded-lg p-3 text-xs shadow-md">
            <div className="font-bold text-center mb-1">ALPHA VERSION</div>
            <p>This is an early version of the platform. Features may not work perfectly and may change without notice.</p>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  <Menu size={24} />
                </button>
                <div className="ml-4 lg:ml-0 flex items-center">
                  <AlertTriangle className="text-red-600 mr-2" size={20} />
                  <div className="text-lg font-semibold text-red-600">Admin Control Panel</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <MeritDisplay />
                <button
                  onClick={handleThemeToggle}
                  className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                >
                  {theme === THEMES.DARK ? (
                    <span>☀️</span>
                  ) : (
                    <span>🌙</span>
                  )}
                </button>
                
                <div className="relative">
                  <div className="flex items-center">
                    <div className="ml-3">
                      <div className="text-sm font-medium">
                        {user?.name} (Admin)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;